import React, { useCallback, useMemo, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ChevronDown from 'assets/svg/ChevronDown';
import type {
    ConstraintColumnType,
    ConstraintFieldType,
    ConstraintTableColumn,
} from '../../../../config/optimizePlanConfig';
import type { ConstraintRowData } from './types';
import { formatNumWithKMBAlt } from 'utils/helperMethods';

type ConstraintTableRow = ConstraintRowData & {
    rowKey: string;
    isTop: boolean;
    parentName?: string;
    children?: ConstraintTableRow[];
};

interface Props {
    columns: ConstraintTableColumn[];
    data: ConstraintRowData[];
    setData: React.Dispatch<React.SetStateAction<ConstraintRowData[]>>;
    isTopLevel: boolean;
}

function formatValue(value: number | null | undefined, fieldType?: ConstraintFieldType): string {
    if (value == null || isNaN(Number(value))) return '-';
    const num = Number(value);
    switch (fieldType) {
        case 'percentage':
            return `${num.toFixed(1)}%`;
        case 'integer':
            return formatNumWithKMBAlt(Math.round(num), 0);
        case 'decimal':
        default:
            return formatNumWithKMBAlt(num, 1);
    }
}

function getDisplayValue(column: ConstraintTableColumn, row: ConstraintRowData): string {
    const fieldType = row.fieldType ?? column.defaultFieldType;

    if (column.type === 'label') return row.label || '-';

    if (column.type === 'range') {
        const rangeData = row.fields[column.key];
        if (rangeData == null || typeof rangeData !== 'object') return '-';
        const { min, max } = rangeData as { min: number | null; max: number | null };
        if (min == null && max == null) return '-';
        return `${formatValue(min, fieldType)} - ${formatValue(max, fieldType)}`;
    }

    const val = row.fields[column.key] as number | string | null | undefined;
    if (val == null) return '-';
    if (typeof val === 'string') return val || '-';
    return formatValue(Number(val), fieldType);
}

interface RangeInputCellProps {
    minVal: number | null;
    maxVal: number | null;
    onMinChange: (val: number | null) => void;
    onMaxChange: (val: number | null) => void;
}

const RangeInputCell: React.FC<RangeInputCellProps> = ({ minVal, maxVal, onMinChange, onMaxChange }) => (
    <div className="flex items-center gap-1.5">
        <input
            type="number"
            placeholder="Min"
            value={minVal ?? ''}
            onChange={(e) => onMinChange(e.target.value === '' ? null : Number(e.target.value))}
            className="w-[70px] h-7 px-2 text-sm border border-[#d9d9d9] rounded focus:outline-none focus:border-[#44338a]"
        />
        <span className="text-[#d9d9d9] select-none">-</span>
        <input
            type="number"
            placeholder="Max"
            value={maxVal ?? ''}
            onChange={(e) => onMaxChange(e.target.value === '' ? null : Number(e.target.value))}
            className="w-[70px] h-7 px-2 text-sm border border-[#d9d9d9] rounded focus:outline-none focus:border-[#44338a]"
        />
    </div>
);

interface NumberInputCellProps {
    value: number | null;
    onChange: (val: number | null) => void;
}

const NumberInputCell: React.FC<NumberInputCellProps> = ({ value, onChange }) => (
    <input
        type="number"
        placeholder="Value"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="w-[140px] h-7 px-2 text-sm border border-[#d9d9d9] rounded focus:outline-none focus:border-[#44338a]"
    />
);

const TYPE_FLEX: Record<Exclude<ConstraintColumnType, 'hidden'>, number> = {
    label: 2,
    value: 1,
    range: 1,
    input: 1.5,
};

const EXPAND_COL_WIDTH = 48;

function computeColumnWidths(cols: ConstraintTableColumn[]): Map<string, string> {
    const visibleCols = cols.filter((c) => c.type !== 'hidden');
    const totalFlex = visibleCols.reduce((sum, c) => sum + TYPE_FLEX[c.type as Exclude<ConstraintColumnType, 'hidden'>], 0);
    const widths = new Map<string, string>();
    for (const col of visibleCols) {
        widths.set(col.key, `${((TYPE_FLEX[col.type as Exclude<ConstraintColumnType, 'hidden'>] / totalFlex) * 100).toFixed(1)}%`);
    }
    return widths;
}

const ConstraintsTable: React.FC<Props> = ({ columns: columnConfig, data, setData, isTopLevel }) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    const columnWidths = useMemo(() => computeColumnWidths(columnConfig), [columnConfig]);

    const dataSource = useMemo<ConstraintTableRow[]>(
        () =>
            data.map((row) => {
                const nestedCount = row.levelDetails?.length ?? 0;
                const isExpandable = nestedCount > 0 && (!isTopLevel || nestedCount > 1);
                return {
                    ...row,
                    rowKey: row.name,
                    isTop: true,
                    children: isExpandable
                        ? row.levelDetails!.map((nested) => ({
                              ...nested,
                              rowKey: `${row.name}-${nested.name}`,
                              isTop: false,
                              parentName: row.name,
                          }))
                        : undefined,
                };
            }),
        [data, isTopLevel],
    );

    const onToggleExpand = useCallback((key: string) => {
        setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    }, []);

    const handleTopRangeChange = useCallback(
        (name: string, isMin: boolean, val: number | null) => {
            setData((prev) =>
                prev.map((r) => (r.name === name ? { ...r, [isMin ? 'min_value' : 'max_value']: val } : r)),
            );
        },
        [setData],
    );

    const handleTopNumberChange = useCallback(
        (name: string, val: number | null) => {
            setData((prev) => prev.map((r) => (r.name === name ? { ...r, single_value: val } : r)));
        },
        [setData],
    );

    const handleNestedRangeChange = useCallback(
        (parentName: string, nestedName: string, isMin: boolean, val: number | null) => {
            setData((prev) =>
                prev.map((r) =>
                    r.name === parentName
                        ? {
                              ...r,
                              levelDetails: r.levelDetails?.map((n) =>
                                  n.name === nestedName ? { ...n, [isMin ? 'min_value' : 'max_value']: val } : n,
                              ),
                          }
                        : r,
                ),
            );
        },
        [setData],
    );

    const handleNestedNumberChange = useCallback(
        (parentName: string, nestedName: string, val: number | null) => {
            setData((prev) =>
                prev.map((r) =>
                    r.name === parentName
                        ? {
                              ...r,
                              levelDetails: r.levelDetails?.map((n) =>
                                  n.name === nestedName ? { ...n, single_value: val } : n,
                              ),
                          }
                        : r,
                ),
            );
        },
        [setData],
    );

    const inputCol = columnConfig.find((c) => c.type === 'input');

    const renderInputCell = useCallback(
        (record: ConstraintTableRow) => {
            if (!inputCol) return null;
            const inputType = record.constraint_type ?? inputCol.defaultInputType ?? 'range';

            if (record.isTop) {
                const showDirectInput = isTopLevel && record.children == null;
                if (!showDirectInput) return <span className="text-[#bfbfbf] select-none">—</span>;

                if (inputType === 'range') {
                    return (
                        <RangeInputCell
                            minVal={record.min_value}
                            maxVal={record.max_value}
                            onMinChange={(val) => handleTopRangeChange(record.name, true, val)}
                            onMaxChange={(val) => handleTopRangeChange(record.name, false, val)}
                        />
                    );
                }
                return (
                    <NumberInputCell
                        value={record.single_value ?? null}
                        onChange={(val) => handleTopNumberChange(record.name, val)}
                    />
                );
            }

            if (inputType === 'range') {
                return (
                    <RangeInputCell
                        minVal={record.min_value}
                        maxVal={record.max_value}
                        onMinChange={(val) => handleNestedRangeChange(record.parentName!, record.name, true, val)}
                        onMaxChange={(val) => handleNestedRangeChange(record.parentName!, record.name, false, val)}
                    />
                );
            }
            return (
                <NumberInputCell
                    value={record.single_value ?? null}
                    onChange={(val) => handleNestedNumberChange(record.parentName!, record.name, val)}
                />
            );
        },
        [inputCol, isTopLevel, handleTopRangeChange, handleTopNumberChange, handleNestedRangeChange, handleNestedNumberChange],
    );

    const tableColumns = useMemo<ColumnsType<ConstraintTableRow>>(
        () => [
            ...columnConfig.filter((col) => col.type !== 'hidden').map((col) => ({
                title: col.label,
                dataIndex: col.key,
                key: col.key,
                width: columnWidths.get(col.key),
                onCell: (record: ConstraintTableRow) => ({
                    className: record.isTop ? 'text-[#18181b]' : 'text-[#595959] !bg-[#f5f5f5]',
                }),
                render: (_: unknown, record: ConstraintTableRow) => {
                    if (col.type === 'input') return renderInputCell(record);
                    return <span className="text-sm">{getDisplayValue(col, record)}</span>;
                },
            })),
            {
                title: '',
                key: '_expand',
                width: EXPAND_COL_WIDTH,
                onCell: (record: ConstraintTableRow) => ({
                    className: record.isTop ? '' : '!bg-[#f5f5f5]',
                }),
                render: (_: unknown, record: ConstraintTableRow) => {
                    if (!record.children) return null;
                    const isExpanded = expandedRowKeys.includes(record.rowKey);
                    return (
                        <span
                            className="cursor-pointer flex items-center justify-center p-1 text-[#595959] hover:text-[#44338a] transition-colors"
                            onClick={() => onToggleExpand(record.rowKey)}
                        >
                            <ChevronDown className={isExpanded ? 'rotate-180' : 'rotate-0'} size={12} />
                        </span>
                    );
                },
            },
        ],
        [columnConfig, columnWidths, expandedRowKeys, onToggleExpand, renderInputCell],
    );

    return (
        <div className="h-full flex flex-col min-h-0 overflow-hidden [&_.ant-table-wrapper]:h-full [&_.ant-table-wrapper_.ant-spin-nested-loading]:h-full [&_.ant-table-wrapper_.ant-spin-container]:h-full [&_.ant-table-wrapper_.ant-table]:h-full [&_.ant-table-wrapper_.ant-table-container]:h-full [&_.ant-table-wrapper_.ant-table-container]:flex [&_.ant-table-wrapper_.ant-table-container]:flex-col [&_.ant-table-wrapper_.ant-table-header]:shrink-0 [&_.ant-table-wrapper_.ant-table-body]:flex-1 [&_.ant-table-wrapper_.ant-table-body]:min-h-0 [&_.ant-table-wrapper_.ant-table-body]:!overflow-y-auto">
            <Table<ConstraintTableRow>
                columns={tableColumns}
                dataSource={dataSource}
                rowKey="rowKey"
                pagination={false}
                size="small"
                sticky
                tableLayout="fixed"
                scroll={{ y: '100%' }}
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                    expandIcon: () => null,
                    indentSize: 0,
                }}
            />
        </div>
    );
};

ConstraintsTable.displayName = 'ConstraintsTable';
export default React.memo(ConstraintsTable);
